import React, { useState } from "react";
import { Alert, Button, Stack, TextField, Typography } from "@mui/material";
import UploadFileOutlinedIcon from "@mui/icons-material/UploadFileOutlined";
import { useTranslation } from "react-i18next";
import { fetchData } from "../../api/client";

function readFileBase64(file) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => {
      const raw = String(reader.result || "");
      const comma = raw.indexOf(",");
      resolve(comma >= 0 ? raw.slice(comma + 1) : raw);
    };
    reader.onerror = () => reject(reader.error || new Error("read"));
    reader.readAsDataURL(file);
  });
}

const ERROR_KEYS = {
  missing_files: "needBoth",
  too_large: "tooLarge",
  bad_cer: "badCer",
  csr: "csr",
  bad_password: "badPass",
  bad_pfx: "badPfx",
  mismatch: "mismatch",
};

/**
 * Envio de .cer + .pfx no Pix Cielo. A API devolve o PEM que o cadastro já grava.
 */
export default function CieloPixCertFilesPanel({ onConverted }) {
  const { t } = useTranslation("settings");
  const p = "paymentMethodsPage.cieloPixFiles";
  const [cerFile, setCerFile] = useState(null);
  const [pfxFile, setPfxFile] = useState(null);
  const [password, setPassword] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [ready, setReady] = useState(false);
  const [holderName, setHolderName] = useState("");

  const handleSubmit = async () => {
    setError("");
    setReady(false);
    setHolderName("");
    if (!cerFile || !pfxFile) {
      setError(t(`${p}.needBoth`));
      return;
    }
    setBusy(true);
    try {
      const [cerBase64, pfxBase64] = await Promise.all([
        readFileBase64(cerFile),
        readFileBase64(pfxFile),
      ]);
      const resp = await fetchData("/payment-methods/cielo-pix-mtls-files", "POST", {
        cerBase64,
        pfxBase64,
        password,
      });
      const data = resp?.data ?? resp;
      const certPem = String(data?.certPem || "").trim();
      const keyPem = String(data?.keyPem || "").trim();
      if (!certPem || !keyPem) {
        setError(t(`${p}.failed`));
        return;
      }
      const name = String(data.holderName || "").trim();
      setHolderName(name);
      setReady(true);
      setPassword("");
      if (typeof onConverted === "function") {
        onConverted({ certPem, keyPem, holderName: data.holderName || "" });
      }
    } catch (e) {
      const code = e?.code || e?.data?.code;
      const key = ERROR_KEYS[code];
      setError(key ? t(`${p}.${key}`) : e?.message || t(`${p}.failed`));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Stack spacing={1.5}>
      <Typography variant="body2" color="text.secondary">
        {t(`${p}.body`)}
      </Typography>

      <Stack direction={{ xs: "column", sm: "row" }} spacing={1} alignItems={{ sm: "center" }}>
        <Button
          component="label"
          variant="outlined"
          startIcon={<UploadFileOutlinedIcon />}
          disabled={busy}
          sx={{ textTransform: "none", justifyContent: "flex-start" }}
        >
          {t(`${p}.cerButton`)}
          <input
            hidden
            type="file"
            accept=".cer,.crt,.pem,application/pkix-cert,application/x-x509-ca-cert"
            onChange={(e) => {
              setCerFile(e.target.files?.[0] || null);
              setReady(false);
              setHolderName("");
              setError("");
            }}
          />
        </Button>
        <Typography variant="body2" color={cerFile ? "text.primary" : "text.secondary"}>
          {cerFile ? cerFile.name : t(`${p}.cerEmpty`)}
        </Typography>
      </Stack>

      <Stack direction={{ xs: "column", sm: "row" }} spacing={1} alignItems={{ sm: "center" }}>
        <Button
          component="label"
          variant="outlined"
          startIcon={<UploadFileOutlinedIcon />}
          disabled={busy}
          sx={{ textTransform: "none", justifyContent: "flex-start" }}
        >
          {t(`${p}.pfxButton`)}
          <input
            hidden
            type="file"
            accept=".pfx,.p12,application/x-pkcs12"
            onChange={(e) => {
              setPfxFile(e.target.files?.[0] || null);
              setReady(false);
              setHolderName("");
              setError("");
            }}
          />
        </Button>
        <Typography variant="body2" color={pfxFile ? "text.primary" : "text.secondary"}>
          {pfxFile ? pfxFile.name : t(`${p}.pfxEmpty`)}
        </Typography>
      </Stack>

      <TextField
        label={t(`${p}.password`)}
        type="password"
        value={password}
        onChange={(e) => setPassword(e.target.value)}
        fullWidth
        size="small"
        autoComplete="new-password"
        disabled={busy}
        helperText={t(`${p}.passwordHelper`)}
      />

      <Button variant="contained" onClick={handleSubmit} disabled={busy} sx={{ alignSelf: "flex-start", textTransform: "none" }}>
        {busy ? t(`${p}.busy`) : t(`${p}.submit`)}
      </Button>

      {ready ? (
        <Alert severity="success">
          {holderName ? t(`${p}.okNamed`, { name: holderName }) : t(`${p}.ok`)}
        </Alert>
      ) : null}
      {error ? <Alert severity="error">{error}</Alert> : null}
    </Stack>
  );
}
